import { getLabelFromId } from '@core/utils/collection';
import type { Collar } from '../../api/collar';
import type { LegacyCollar } from '../../api/legacyCollar';
import type { Vestment } from '../../api/vestment';
import { VestmentCategory } from '../enum/vestmentCategory';
import {
  type VestmentType,
  getCategory,
  isV1VestmentType,
} from '../enum/vestmentType';
import { getChasubleFeatures } from './chasuble';

export type CollarFlag = Extract<
  keyof LegacyCollar,
  'isLegacyOrphrey' | 'isLegacyPlain' | 'isAlb'
>;

export type CollarOption = Pick<Collar | LegacyCollar, 'id' | 'name'>;

export function getCollarFlagForVestmentType(
  vestmentType: VestmentType | undefined
): CollarFlag | undefined {
  if (!vestmentType) {
    return undefined;
  }
  switch (getCategory(vestmentType)) {
    case VestmentCategory.Chasuble:
      return getChasubleFeatures(vestmentType)?.collarFlag;
    case VestmentCategory.Alb:
      return 'isAlb';
    default:
      return undefined;
  }
}

export function filterCollarsByFlag(
  collars: LegacyCollar[],
  flag: CollarFlag
): LegacyCollar[] {
  return collars.filter((collar) => Boolean(collar[flag]));
}

/** Non-legacy chasubles pick from the vestment's own collars instead of legacy collars. */
export function usesVestmentCollars(
  vestmentType: VestmentType | null | undefined
): boolean {
  return (
    !!vestmentType &&
    getCategory(vestmentType) === VestmentCategory.Chasuble &&
    !isV1VestmentType(vestmentType)
  );
}

/** Collars an item of the vestment can pick from. */
export function getCollarOptions(
  vestment: Vestment | undefined,
  legacyCollars: LegacyCollar[]
): CollarOption[] {
  if (vestment && usesVestmentCollars(vestment.type)) {
    return vestment.collars ?? [];
  }
  const flag = getCollarFlagForVestmentType(vestment?.type);
  return flag ? filterCollarsByFlag(legacyCollars, flag) : legacyCollars;
}

/** Name of the item's collar; legacy collars are looked up unfiltered so older picks still show. */
export function getCollarLabel(
  vestment: Vestment | undefined,
  legacyCollars: LegacyCollar[],
  collarId: string
): string | null {
  const collars: CollarOption[] =
    vestment && usesVestmentCollars(vestment.type)
      ? (vestment.collars ?? [])
      : legacyCollars;
  return getLabelFromId(collars, collarId);
}

/** Case-insensitive, like the per-vestment unique name constraint. */
export function findCollarByName(
  collars: CollarOption[],
  name: string | null
): CollarOption | undefined {
  const key = name?.trim().toLowerCase();
  if (!key) {
    return undefined;
  }
  return collars.find((collar) => collar.name.trim().toLowerCase() === key);
}

/** Default collar of `collarDefaults` chasuble types; the backend locks its name. */
export const CHINESE_COLLAR_NAME = 'Chinese Collar';

export function isChineseCollar(collar: Pick<Collar, 'name'>): boolean {
  return collar.name === CHINESE_COLLAR_NAME;
}

/** Per-vestment Chinese Collar of `collarDefaults` types; the backend blocks renaming and deleting it. */
export function isRequiredCollar(
  collar: Pick<Collar, 'name'>,
  vestmentType: VestmentType
): boolean {
  return (
    isChineseCollar(collar) &&
    (getChasubleFeatures(vestmentType)?.collarDefaults ?? false)
  );
}
